<template>
  <div class="modern-homepage">
    <!-- 动态背景 -->
    <div class="animated-background">
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
        <div class="shape shape-5"></div>
      </div>
      <div class="grid-overlay"></div>
    </div>

    <!-- 导航栏 -->
    <nav class="top-nav">
      <div class="nav-container">
        <div class="nav-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span class="brand-text">{{ $t('nav.brand') }}</span>
        </div>
        <div class="nav-actions">
          <div class="language-switcher">
            <button 
              @click="toggleLanguage" 
              class="language-btn"
              :title="$t('common.language')"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M12.87,15.07L10.33,12.56L10.36,12.53C12.1,10.59 13.34,8.36 14.07,6H17V4H10V2H8V4H1V6H12.17C11.5,7.92 10.44,9.75 9,11.35C8.07,10.32 7.3,9.19 6.69,8H4.69C5.42,9.63 6.42,11.17 7.67,12.56L2.58,17.58L4,19L9,14L12.11,17.11L12.87,15.07M18.5,10H16.5L12,22H14L15.12,19H19.87L21,22H23L18.5,10M15.88,17L17.5,12.67L19.12,17H15.88Z"/>
              </svg>
              {{ currentLanguage === 'zh' ? 'EN' : '中' }}
            </button>
          </div>
          <router-link to="/pricing" class="nav-btn">{{ $t('pricing.nav.pricing') }}</router-link>
          <router-link to="/blog" class="nav-btn">{{ $t('nav.docs') }}</router-link>
        </div>
      </div>
    </nav>

    <!-- 主要内容容器 -->
    <div class="main-container">
      <!-- Hero 区域 -->
      <section class="hero-section">
        <div class="hero-content">
          <div class="hero-badge">
            <span class="badge-text">{{ $t('hero.badge') }}</span>
          </div>
          <h1 class="hero-title">
            {{ $t('hero.title') }}
            <span class="gradient-text">{{ $t('hero.titleHighlight') }}</span>
          </h1>
          <p class="hero-description">
            {{ $t('hero.description') }}
          </p>
          
          <!-- 快速开始表单 -->
          <div class="quick-start-form">
            <div class="form-container">
              <div class="form-header">
                <h3>{{ $t('hero.quickStart.title') }}</h3>
                <p>{{ $t('hero.quickStart.subtitle') }}</p>
              </div>
              
              <form @submit.prevent="onPreview" class="url-form">
                <div class="input-group">
                  <input 
                    v-model="dxfUrl"
                    type="url" 
                    :placeholder="$t('hero.quickStart.urlPlaceholder')"
                    class="url-input"
                    required
                  >
                  <button type="submit" class="submit-btn" :disabled="!dxfUrl">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M1,12A11,11 0 0,1 12,1A11,11 0 0,1 23,12A11,11 0 0,1 12,23A11,11 0 0,1 1,12M15,8L10.5,16L9,14.5L11.5,12L9,9.5L15,8Z"/>
                    </svg>
                    {{ $t('hero.quickStart.submitButton') }}
                  </button>
                </div>
                
                <div class="form-options">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="showLayer">
                    <span class="checkmark"></span>
                    {{ $t('hero.quickStart.showLayerLabel') }}
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="showBack">
                    <span class="checkmark"></span>
                    {{ $t('hero.quickStart.showBackLabel') }}
                  </label>
                  <input 
                    v-model="customTitle" 
                    type="text" 
                    :placeholder="$t('hero.quickStart.titlePlaceholder')"
                    class="title-input"
                  >
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <!-- 视觉展示区 -->
        <div class="hero-visual">
          <div class="demo-viewer">
            <div class="viewer-frame">
              <div class="viewer-header">
                <div class="viewer-controls">
                  <div class="control-dot red"></div>
                  <div class="control-dot yellow"></div>
                  <div class="control-dot green"></div>
                </div>
                <div class="viewer-title">drawing.dxf</div>
              </div>
              <div class="viewer-content">
                <div class="drawing-preview">
                  <!-- 模拟的CAD图纸预览 -->
                  <svg viewBox="0 0 300 200" class="mock-drawing">
                    <defs>
                      <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#334155" stroke-width="0.5" opacity="0.3"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)"/>
                    <g stroke="#06b6d4" stroke-width="2" fill="none">
                      <rect x="50" y="40" width="120" height="80" rx="4"/>
                      <circle cx="110" cy="80" r="25"/>
                      <path d="M 180 50 L 240 50 L 240 110 L 180 110 Z"/>
                      <line x1="200" y1="50" x2="200" y2="110"/>
                      <line x1="180" y1="80" x2="240" y2="80"/>
                    </g>
                    <g stroke="#f59e0b" stroke-width="1.5" fill="none">
                      <path d="M 60 140 Q 110 120 160 140 T 240 150"/>
                      <circle cx="80" cy="160" r="8"/>
                      <circle cx="140" cy="165" r="6"/>
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 特性展示区 -->
      <section class="features-section">
        <div class="section-header">
          <h2 class="section-title">{{ $t('features.title') }}</h2>
          <p class="section-subtitle">{{ $t('features.subtitle') }}</p>
        </div>
        
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M15,18V16H6V18H15M18,14V12H6V14H18Z"/>
              </svg>
            </div>
            <h3>{{ $t('features.fastLoading.title') }}</h3>
            <p>{{ $t('features.fastLoading.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
              </svg>
            </div>
            <h3>{{ $t('features.layerControl.title') }}</h3>
            <p>{{ $t('features.layerControl.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4H5V21H19V9Z"/>
              </svg>
            </div>
            <h3>{{ $t('features.cloudAccess.title') }}</h3>
            <p>{{ $t('features.cloudAccess.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M17,12C17,14.42 15.28,16.44 13,16.9V21H11V16.9C8.72,16.44 7,14.42 7,12C7,9.58 8.72,7.56 11,7.1V3H13V7.1C15.28,7.56 17,9.58 17,12M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/>
              </svg>
            </div>
            <h3>{{ $t('features.preciseMeasurement.title') }}</h3>
            <p>{{ $t('features.preciseMeasurement.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
              </svg>
            </div>
            <h3>{{ $t('features.secureReliable.title') }}</h3>
            <p>{{ $t('features.secureReliable.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path fill="currentColor" d="M18,15H16V17H18M18,11H16V13H18M20,19H12V17H14V15H12V13H14V11H12V9H20M10,7H8V5H10M10,11H8V9H10M10,15H8V13H10M10,19H8V17H10M6,7H4V5H6M6,11H4V9H6M6,15H4V13H6M6,19H4V17H6M12,7V3H2V21H22V7H12Z"/>
              </svg>
            </div>
            <h3>{{ $t('features.responsiveDesign.title') }}</h3>
            <p>{{ $t('features.responsiveDesign.description') }}</p>
          </div>
        </div>
      </section>

      <!-- API文档区域 -->
      <section class="api-section">
        <div class="section-header">
          <h2 class="section-title">{{ $t('api.title') }}</h2>
          <p class="section-subtitle">{{ $t('api.subtitle') }}</p>
        </div>

        <div class="api-examples">
          <div class="api-card">
            <div class="api-header">
              <span class="api-method">{{ $t('common.get') }}</span>
              <span class="api-endpoint">/viewer</span>
            </div>
            <div class="api-description">{{ $t('api.basic.title') }}</div>
            <div class="api-example">
              <div class="example-title">{{ $t('common.example') }}</div>
              <code class="code-block">
                {{ $t('api.basic.example') }}
              </code>
            </div>
          </div>

          <div class="api-card">
            <div class="api-header">
              <span class="api-method">{{ $t('common.get') }}</span>
              <span class="api-endpoint">/viewer?showLayer=true</span>
            </div>
            <div class="api-description">{{ $t('api.showLayer.title') }}</div>
            <div class="api-example">
              <div class="example-title">{{ $t('common.example') }}</div>
              <code class="code-block">
                {{ $t('api.showLayer.example') }}
              </code>
            </div>
          </div>

          <div class="api-card">
            <div class="api-header">
              <span class="api-method">{{ $t('common.get') }}</span>
              <span class="api-endpoint">/viewer?title=custom</span>
            </div>
            <div class="api-description">{{ $t('api.customTitle.title') }}</div>
            <div class="api-example">
              <div class="example-title">{{ $t('common.example') }}</div>
              <code class="code-block">
                {{ $t('api.customTitle.example') }}
              </code>
            </div>
          </div>

          <div class="api-card">
            <div class="api-header">
              <span class="api-method">{{ $t('common.get') }}</span>
              <span class="api-endpoint">/viewer?showBack=false</span>
            </div>
            <div class="api-description">{{ $t('api.showBack.title') }}</div>
            <div class="api-example">
              <div class="example-title">{{ $t('common.example') }}</div>
              <code class="code-block">
                {{ $t('api.showBack.example') }}
              </code>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 页脚 -->
    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span>{{ $t('footer.brand') }}</span>
        </div>
        <div class="footer-links">
          <router-link to="/blog">{{ $t('footer.docs') }}</router-link>
          <a href="#">{{ $t('footer.about') }}</a>
          <a href="mailto:fn9170@gmail.com">{{ $t('footer.contact') }}</a>
        </div>
        <div class="footer-copyright">
          {{ $t('footer.copyright') }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { changeLanguage, getCurrentLanguage } from '@/i18n'

export default {
  name: 'HomePage',
  data() {
    return {
      dxfUrl: '',
      customTitle: '',
      showLayer: false,
      showBack: true,
      currentLanguage: getCurrentLanguage()
    }
  },
  methods: {
    onPreview() {
      if (this.dxfUrl) {
        const query = { dxfUrl: this.dxfUrl }
        if (this.customTitle) {
          query.title = this.customTitle
        }
        if (this.showLayer) {
          query.showLayer = 'true'
        }
        if (!this.showBack) {
          query.showBack = 'false'
        }
        this.$router.push({
          path: '/viewer',
          query: query
        })
      }
    },
    
    toggleLanguage() {
      const newLanguage = this.currentLanguage === 'en' ? 'zh' : 'en'
      this.currentLanguage = changeLanguage(newLanguage)
      this.updateSEO()
    },
    
    updateSEO() {
      // 更新页面标题
      document.title = this.$t('seo.title')
      
      // 更新meta标签
      const updateMetaTag = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`)
        if (!element) {
          element = document.createElement('meta')
          element.setAttribute('name', name)
          document.head.appendChild(element)
        }
        element.setAttribute('content', content)
      }
      
      updateMetaTag('description', this.$t('seo.description'))
      updateMetaTag('keywords', this.$t('seo.keywords'))
    }
  },
  
  mounted() {
    this.updateSEO()
  },
  
  watch: {
    '$i18n.locale'() {
      this.updateSEO()
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.modern-homepage {
  min-height: 100vh;
  background: #0f172a;
  position: relative;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ===== 动态背景 ===== */
.animated-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: 
    radial-gradient(circle at 20% 10%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 60%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

.floating-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
  filter: blur(40px);
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 200px;
  height: 200px;
  top: 60%;
  right: 10%;
  animation-delay: 5s;
}

.shape-3 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  left: 50%;
  animation-delay: 10s;
}

.shape-4 {
  width: 100px;
  height: 100px;
  top: 30%;
  right: 30%;
  animation-delay: 15s;
}

.shape-5 {
  width: 250px;
  height: 250px;
  bottom: 10%;
  right: 60%;
  animation-delay: 8s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(20px) rotate(240deg); }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
  animation: gridMove 40s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* ===== 导航栏 ===== */
.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  padding: 1rem 0;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.brand-icon {
  color: #3b82f6;
  transition: transform 0.3s ease;
}

.brand-icon:hover {
  transform: scale(1.1);
}

.brand-text {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.language-switcher {
  position: relative;
}

.language-btn {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #e2e8f0;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  min-width: 60px;
  justify-content: center;
}

.language-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
  transform: translateY(-1px);
}

.language-btn svg {
  opacity: 0.8;
}

.nav-btn {
  background: transparent;
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none !important;
}

.nav-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
}

/* ===== 主容器 ===== */
.main-container {
  position: relative;
  z-index: 1;
  padding-top: 100px;
}

/* ===== Hero 区域 ===== */
.hero-section {
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  min-height: 80vh;
}

.hero-content {
  max-width: 600px;
}

.hero-badge {
  display: inline-block;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 50px;
  padding: 0.5rem 1.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
}

.badge-text {
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 500;
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 2rem;
  color: #f1f5f9;
  letter-spacing: -0.02em;
}

.gradient-text {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% 200%;
  animation: gradientShift 3s ease-in-out infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-description {
  font-size: 1.2rem;
  line-height: 1.7;
  color: #94a3b8;
  margin-bottom: 3rem;
}

/* ===== 快速开始表单 ===== */
.quick-start-form {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 24px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.form-header h3 {
  color: #f1f5f9;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.form-header p {
  color: #94a3b8;
  margin-bottom: 2rem;
}

.input-group {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.url-input {
  flex: 1;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  color: #f1f5f9;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.url-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.url-input::placeholder {
  color: #64748b;
}

.submit-btn {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(59, 130, 246, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-options {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.8);
  cursor: pointer;
  position: relative;
}

.checkbox-label input[type="checkbox"]:checked {
  background: #3b82f6;
  border-color: #3b82f6;
}

.checkbox-label input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.title-input {
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #f1f5f9;
  font-size: 0.9rem;
  min-width: 200px;
}

.title-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.title-input::placeholder {
  color: #64748b;
}

/* ===== 视觉展示区 ===== */
.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.demo-viewer {
  perspective: 1000px;
}

.viewer-frame {
  background: rgba(30, 41, 59, 0.9);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 16px;
  overflow: hidden;
  width: 500px;
  height: 350px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transform: rotateY(-5deg) rotateX(2deg);
  transition: transform 0.3s ease;
}

.viewer-frame:hover {
  transform: rotateY(0deg) rotateX(0deg) scale(1.02);
}

.viewer-header {
  background: rgba(15, 23, 42, 0.9);
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
}

.viewer-controls {
  display: flex;
  gap: 0.5rem;
}

.control-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.control-dot.red { background: #ef4444; }
.control-dot.yellow { background: #f59e0b; }
.control-dot.green { background: #10b981; }

.viewer-title {
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 500;
}

.viewer-content {
  padding: 1rem;
  height: 100%;
  background: #0f172a;
}

.mock-drawing {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

/* ===== 特性展示区 ===== */
.features-section {
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #94a3b8;
  max-width: 600px;
  margin: 0 auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.feature-card:hover::before {
  opacity: 1;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(59, 130, 246, 0.4);
}

.feature-icon {
  color: #3b82f6;
  margin-bottom: 1.5rem;
}

.feature-card h3 {
  color: #f1f5f9;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.feature-card p {
  color: #94a3b8;
  line-height: 1.6;
}

/* ===== API文档区域 ===== */
.api-section {
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: rgba(15, 23, 42, 0.3);
  border-radius: 32px;
  border: 1px solid rgba(59, 130, 246, 0.1);
}

.api-examples {
  display: grid;
  gap: 2rem;
}

.api-card {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(20px);
}

.api-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.api-method {
  background: #10b981;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.api-endpoint {
  color: #f1f5f9;
  font-family: 'SF Mono', monospace;
  font-size: 1.1rem;
}

.api-description {
  color: #94a3b8;
  margin-bottom: 1.5rem;
}

.example-title {
  color: #94a3b8;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.code-block {
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  padding: 1rem;
  color: #06b6d4;
  font-family: 'SF Mono', monospace;
  font-size: 0.9rem;
  display: block;
  overflow-x: auto;
}

/* ===== 页脚 ===== */
.site-footer {
  background: rgba(15, 23, 42, 0.9);
  border-top: 1px solid rgba(59, 130, 246, 0.1);
  padding: 3rem 2rem 2rem;
  position: relative;
  z-index: 1;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #f1f5f9;
  font-weight: 600;
}

.footer-brand .brand-icon {
  color: #3b82f6;
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: #3b82f6;
}

.footer-copyright {
  color: #64748b;
  font-size: 0.9rem;
}

/* ===== 响应式设计 ===== */
@media (max-width: 1024px) {
  .hero-section {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
  
  .hero-title {
    font-size: 3rem;
  }
  
  .viewer-frame {
    width: 400px;
    height: 280px;
  }
}

@media (max-width: 768px) {
  .nav-container {
    padding: 0 1rem;
  }
  
  .hero-section {
    padding: 2rem 1rem;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .input-group {
    flex-direction: column;
  }
  
  .submit-btn {
    width: 100%;
    justify-content: center;
  }
  
  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .viewer-frame {
    width: 300px;
    height: 200px;
  }
  
  .footer-container {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .quick-start-form {
    padding: 1.5rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .viewer-frame {
    width: 250px;
    height: 180px;
  }
}
</style>